import { sha256 } from '@src/utility/sha256';

import type { TranscriptElement } from '@src/types.ts/transcript';

/**
 * Stable hash over the *set* of topic titles. Order-independent (titles are
 * trimmed, deduped, and sorted before hashing) so reordering or accidental
 * duplicates upstream don't invalidate the cache.
 */
export function computeTopicsHash(topics: string[]): string {
  const sorted = Array.from(new Set(topics.map(t => t.trim()))).sort();
  return sha256(sorted.join(' '));
}

/**
 * Structural digest of a transcript keyed on paragraph boundaries. Captures:
 *  - the ordered set of paragraph_ids
 *  - per-paragraph word count + first/last word
 *
 * This is deliberately coarser than element-level. The hash is **stable**
 * across middle-word edits within a paragraph that preserve word count,
 * first word, and last word (the typical "rename a noun" or "fix a typo"
 * case where the elements array length and boundary words stay the same).
 *
 * The hash **invalidates** on any of:
 *  - paragraph split/merge (paragraph_id sequence changes)
 *  - elements added or removed from the array (word count changes)
 *  - first or last word of a paragraph being edited
 */
export function computeTranscriptHash(elements: TranscriptElement[]): string {
  if (!elements || elements.length === 0) return sha256('');

  const perParagraph: {
    paragraphId: string;
    firstWord: string;
    lastWord: string;
    wordCount: number;
  }[] = [];

  let current: {
    paragraphId: string;
    firstWord: string;
    lastWord: string;
    wordCount: number;
  } | null = null;

  for (const el of elements) {
    if (el.type !== 'text') continue;
    const pid = el.paragraph_id;
    if (!pid) continue;
    const word = typeof el.value === 'string' ? el.value : '';

    if (!current || current.paragraphId !== pid) {
      if (current) perParagraph.push(current);
      current = {
        paragraphId: pid,
        firstWord: word,
        lastWord: word,
        wordCount: 1,
      };
    } else {
      current.lastWord = word;
      current.wordCount += 1;
    }
  }
  if (current) perParagraph.push(current);

  const digest = perParagraph
    .map(p => `${p.paragraphId}|${p.wordCount}|${p.firstWord}|${p.lastWord}`)
    .join('\n');
  return sha256(digest);
}
